/**
 * Landing feature — EN copy (Stitch rebuild, DEC-051).
 *
 * Covers BOTH compositions from the extraction contract:
 *   - light theme  → landing_page_career_intelligence_animated  (`light.*`)
 *   - dark theme   → landing_page_guided_intelligence_refined   (`dark.*`)
 * plus shared header/footer/hero/pricing/FAQ copy. Stitch placeholder names
 * (CareerAura / VALENCE) and overclaims ("neural engine/core") are replaced
 * with truthful Workerize PRD messaging. No invented prices (Q7).
 */

export const landingEn = {
  // Header / footer (shared)
  'landing.nav.pathway': 'Platform',
  'landing.nav.intelligence': 'Intelligence',
  'landing.nav.pricing': 'Pricing',
  'landing.nav.login': 'Sign in',
  'landing.nav.getStarted': 'Get started',
  'landing.nav.menu': 'Menu',
  'landing.scrollTop.label': 'Back to top',
  'landing.footer.tagline':
    'The intelligence layer that turns work into upward motion — private, connected, and bilingual.',
  'landing.footer.col.platform': 'Platform',
  'landing.footer.platform.1': 'Signal mapping',
  'landing.footer.platform.2': 'Growth paths',
  'landing.footer.platform.3': 'Workforce intelligence',
  'landing.footer.col.company': 'Company',
  'landing.footer.company.1': 'Privacy',
  'landing.footer.company.2': 'Contact',
  'landing.footer.company.3': 'Terms',
  'landing.footer.rights': 'All rights reserved.',

  // Hero (shared)
  'landing.hero.badge': 'AI-native talent intelligence',
  'landing.hero.lead': 'Empowering 1 Million',
  'landing.hero.word.1': 'Talents',
  'landing.hero.word.2': 'Professionals',
  'landing.hero.word.3': 'Futures',
  'landing.hero.tail': 'by 2030. Starting from Jordan.',
  'landing.hero.signal.signals': 'Signals',
  'landing.hero.signal.clarity': 'Clarity',
  'landing.hero.signal.growth': 'Growth',
  'landing.hero.subtitle':
    'Workerize.ai helps individuals discover, validate, and grow their skills through AI-powered career intelligence, connecting talent with opportunities across Jordan and the Middle East.',
  'landing.hero.ctaPrimary': 'Get started free',
  'landing.hero.ctaSecondary': 'Explore the platform',
  'landing.hero.mockup.score': 'Employability',
  'landing.hero.mockup.skills': 'Top skills',
  'landing.hero.mockup.match': 'Career match',
  'landing.hero.mockup.live': '+3 new roles',

  // Dashboard mockup alt
  'landing.mockup.alt': 'Workerize Talent Intelligence Platform dashboard preview',

  // Pricing (shared) — no invented prices (Q7)
  'landing.pricing.heading': 'Guided growth at any scale',
  'landing.pricing.toggle.monthly': 'Monthly',
  'landing.pricing.toggle.yearly': 'Yearly',
  'landing.pricing.toggle.preview': 'Preview',
  'landing.pricing.note': 'Pricing is announced at launch.',
  'landing.pricing.tier1.name': 'Explorer',
  'landing.pricing.tier1.price': 'Free',
  'landing.pricing.tier1.f1': 'Personal skill signal map',
  'landing.pricing.tier1.f2': 'One active growth path',
  'landing.pricing.tier1.f3': 'Community insights',
  'landing.pricing.tier1.cta': 'Get started',
  'landing.pricing.tier2.name': 'Growth',
  'landing.pricing.tier2.price': 'Coming soon',
  'landing.pricing.tier2.badge': 'Most popular',
  'landing.pricing.tier2.f1': 'Full relational signal map',
  'landing.pricing.tier2.f2': 'Unlimited growth paths',
  'landing.pricing.tier2.f3': 'Proactive guided insights',
  'landing.pricing.tier2.f4': 'Priority support',
  'landing.pricing.tier2.cta': 'Join early access',
  'landing.pricing.tier3.name': 'Teams',
  'landing.pricing.tier3.price': 'Custom',
  'landing.pricing.tier3.f1': 'Workforce intelligence',
  'landing.pricing.tier3.f2': 'Readiness across the team',
  'landing.pricing.tier3.f3': 'Internal mobility maps',
  'landing.pricing.tier3.cta': 'Custom for teams',

  // FAQ (shared; light uses the first 3)
  'landing.faq.heading': 'Frequently asked',
  'landing.faq.q1': 'Who is Workerize for?',
  'landing.faq.a1':
    'Workerize is for professionals navigating non-linear careers and for talent teams who want to understand and unlock the potential inside their organizations.',
  'landing.faq.q2': 'How does the guidance work?',
  'landing.faq.a2':
    'Workerize models the relationships between your skills, goals, and market signals, then suggests the next logical move based on your unique trajectory — not keyword matching.',
  'landing.faq.q3': 'Can individuals use it?',
  'landing.faq.a3':
    'Yes. The Explorer and Growth plans are built for individual career management.',
  'landing.faq.q4': 'Can teams use it?',
  'landing.faq.a4':
    'Yes. The Teams plan lets leaders see aggregate signal maps across the organization to support internal mobility and retention.',
  'landing.faq.q5': 'Is my data private?',
  'landing.faq.a5':
    'Workerize is built privacy-first. Your skill maps and growth explorations are yours alone, and we do not sell personal data.',
  'landing.faq.q6': 'Do you support English and Arabic?',
  'landing.faq.a6':
    'Yes. Workerize is fully bilingual and RTL-ready, built for MENA and global talent.',

  // ---- DARK composition (guided_intelligence_refined) ----
  'landing.dark.intel.lead': 'The connected',
  'landing.dark.intel.accent': 'signal network',
  'landing.dark.intel.body':
    'Move beyond static profiles. Workerize continuously connects data points from your professional experience to illuminate your most effective path forward.',
  'landing.dark.intel.card1.title': 'Relational mapping',
  'landing.dark.intel.card1.body':
    'See how every skill connects to broader market opportunities.',
  'landing.dark.intel.card2.title': 'Guided insights',
  'landing.dark.intel.card2.body':
    'Receive proactive guidance as your signal map evolves.',
  'landing.dark.node.skills': 'SKILLS',
  'landing.dark.node.goals': 'GOALS',
  'landing.dark.node.experience': 'EXPERIENCE',
  'landing.dark.node.learning': 'LEARNING',
  'landing.dark.node.opportunities': 'OPPORTUNITIES',
  'landing.dark.node.insights': 'INSIGHTS',
  'landing.dark.features.heading': 'Built for modern trajectories',
  'landing.dark.features.sub': 'Precision intelligence that adapts to your growth in real time.',
  'landing.dark.feature1.title': 'Skill signal mapping',
  'landing.dark.feature1.body':
    'Translate raw experience into granular signals that reveal your true competitive edge.',
  'landing.dark.feature2.title': 'Personalized growth paths',
  'landing.dark.feature2.body':
    'Navigational blueprints that adapt as you gain skills and market conditions shift.',
  'landing.dark.feature3.title': 'Private by design',
  'landing.dark.feature3.body':
    'Your career exploration stays your own until you decide to share it.',
  'landing.dark.feature4.title': 'Bilingual career context',
  'landing.dark.feature4.body':
    'First-class English and Arabic with deep regional context for MENA markets.',
  'landing.dark.feature5.title': 'Team talent visibility',
  'landing.dark.feature5.body':
    'Identify density, gaps, and internal growth opportunities across your organization.',
  'landing.dark.feature6.title': 'Market-aware insights',
  'landing.dark.feature6.body':
    'Stay ahead of shifts with analysis of skill demand and market trends.',
  'landing.dark.cta.heading': 'Ready to map your growth?',
  'landing.dark.cta.button': 'Get started free',

  // ---- LIGHT composition (career_intelligence_animated) ----
  'landing.light.intel.lead': 'The intelligence',
  'landing.light.intel.accent': 'signal network',
  'landing.light.intel.body':
    'Workerize continuously analyzes the signals across your professional life to map the most effective path forward.',
  'landing.light.intel.point1': 'Real-time skill node mapping',
  'landing.light.intel.point2': 'Predictive trajectory modeling',
  'landing.light.intel.point3': 'Pattern recognition across roles',
  'landing.light.node.skills': 'SKILLS',
  'landing.light.node.experience': 'EXPERIENCE',
  'landing.light.node.goals': 'GOALS',
  'landing.light.showcase.step1.title': 'Skill signal mapping',
  'landing.light.showcase.step1.body':
    'Deconstruct your experience into granular data points for precise matching.',
  'landing.light.showcase.step2.title': 'Personalized growth paths',
  'landing.light.showcase.step2.body':
    'Step-by-step blueprints tailored to your ambition and current capability.',
  'landing.light.showcase.step3.title': 'Guided insights',
  'landing.light.showcase.step3.body':
    'Timely guidance when new opportunities align with your trajectory.',
  'landing.light.showcase.panel.label': 'Next logical move',
  'landing.light.showcase.panel.role': 'Project Lead · Innovation Hub',
  'landing.light.showcase.panel.alignment': '78% alignment',
  'landing.light.showcase.panel.gaps': '2 skill gaps found',
  'landing.light.bento.heading': 'Engineered for evolution',
  'landing.light.bento.sub':
    'Traditional career platforms are static. Workerize is dynamic, intelligent, and built for the shifts of the AI age.',
  'landing.light.bento.card1.title': 'Non-linear intelligence',
  'landing.light.bento.card1.body':
    'Careers are ecosystems, not ladders. Workerize maps every pivot, side-step, and leap across domains.',
  'landing.light.bento.card1.tag1': 'Predictive',
  'landing.light.bento.card1.tag2': 'Relational',
  'landing.light.bento.card2.title': 'Zero-trust privacy',
  'landing.light.bento.card2.body':
    'Your career data is your own. Explore paths without broadcasting to recruiters until you are ready.',
  'landing.light.bento.card3.title': 'Bilingual core',
  'landing.light.bento.card3.body':
    'Built from the ground up for high-growth talent in both English and Arabic.',
  'landing.light.bento.card4.title': 'Market signal analysis',
  'landing.light.bento.card4.body':
    'We track real-time economic and skill-demand shifts so your growth path stays relevant.',
  'landing.light.bento.card4.cta': 'View the live engine',
} as const;

export type LandingEn = typeof landingEn;
